import { ThesisSubmission, ThesisSubmissionExaminer, ThesisSubmissionIterationStatus } from '@rdm/db'

import { AuthUser } from '../common.js'
import {
  actionNotificationData,
  ThesisActionNotificationActionIds,
  ThesisExaminerActionNotificationDataType,
} from './examiners/actionNotificationData.js'
import { getNominatedExaminerCount } from './examiners/getThesisExaminerCount.js'
import { getIterationState, getIterationStateByCode, NotificationState } from './iterationState.js'
import {
  isActiveThesisExaminer,
  isAssignedThesisCandidate,
  isAssignedThesisPrincipalAdvisor,
} from './userAccessChecker.js'

export const replacePlaceholdersInString = (template: string, values: Record<string, string>): string => {
  return template.replace(/{{(.*?)}}/g, (_, key) => values[key] || '')
}

type Template = {
  [key: string]: unknown
}

type IterationStateMessage = {
  title: string
  message: string
  action: NotificationState.CANDIDATE_SUBMITTING | NotificationState.ACTION_REQUIRED | NotificationState.NO_ACTION
}

export const replacePlaceholdersInObject = (
  template: Template,
  dynamicValues: Record<string, string>,
): IterationStateMessage => {
  const result: Template = {}

  Object.keys(template).forEach((key) => {
    const value = template[key]

    if (typeof value === 'string') {
      // If the value is a string, replace placeholders
      result[key] = replacePlaceholdersInString(value, dynamicValues)
    } else if (typeof value === 'object' && value !== null) {
      // If the value is an object, recurse
      result[key] = replacePlaceholdersInObject(value as Template, dynamicValues)
    } else {
      // If the value is neither a string nor an object, copy as-is
      result[key] = value
    }
  })

  return {
    title: typeof result.title === 'string' ? result.title : '',
    message: typeof result.message === 'string' ? result.message : '',
    action: (result.action as IterationStateMessage['action']) ?? NotificationState.NO_ACTION,
  }
}

/**
 * Check and get the thesis examiner nomination action notifications for right user
 */
export const getExaminerActionNotifications = (
  submission: Pick<ThesisSubmission, 'id'> & {
    thesisSubmissionExaminers: Array<
      Pick<ThesisSubmissionExaminer, 'id' | 'userId' | 'fullName' | 'candidateCOIStatements' | 'status'> & {
        user: { displayName: string } | null
      }
    >
  } & { creator: { id: number; displayName: string } } & { principalAdvisor: { id: number } } & {
    chair: { id: number } | null
  } & {
    currentIteration: {
      currentStatus: Pick<ThesisSubmissionIterationStatus, 'status' | 'outcome'>
    }
  },
  currentUser: AuthUser,
) => {
  const actions: ThesisExaminerActionNotificationDataType[] = []

  const nominatedExaminersCount = getNominatedExaminerCount(submission.thesisSubmissionExaminers)
  const isAssignedPa = isAssignedThesisPrincipalAdvisor(currentUser, submission.principalAdvisor?.id)
  const isAssignedCandidate = isAssignedThesisCandidate(currentUser, submission.creator.id)
  const isGS = currentUser.roles.includes('grad-school-user')
  const isThesisExaminer = submission.thesisSubmissionExaminers.find((examiner) =>
    examiner?.user ? isActiveThesisExaminer(currentUser, examiner?.userId, examiner?.status) : false,
  )

  const currentIterationState = getIterationState({
    status: submission.currentIteration.currentStatus.status,
    outcome: submission.currentIteration.currentStatus.outcome,
  })

  const chairAssigned = !!submission.chair

  // @TODO future improvement: This could be refactored down to a single reduce/filter function where it checks for the user role and the examiner status
  actionNotificationData(submission.creator.displayName).map((notification) => {
    // PA actions
    if (isAssignedPa) {
      if (chairAssigned === false && currentIterationState.thesisState !== 'FINAL') {
        if (notification.actionId === ThesisActionNotificationActionIds.ASSIGN_CHAIR) {
          actions.push(notification)
        }
      }

      if (currentIterationState.thesisState === 'INITIAL') {
        if (
          notification.actionId === ThesisActionNotificationActionIds.NOMINATE_EXAMINERS &&
          nominatedExaminersCount === 0
        ) {
          actions.push(notification)
        }

        if (
          notification.actionId === ThesisActionNotificationActionIds.NOMINATE_ONE_EXAMINER &&
          nominatedExaminersCount === 1
        ) {
          actions.push(notification)
        }
      }

      submission.thesisSubmissionExaminers.map((examiner) => {
        if (
          notification.actionId === ThesisActionNotificationActionIds.PROVIDE_ADDITIONAL_EXAMINER_DETAILS &&
          examiner.status === 'INITIAL_CHANGE_REQUEST_BY_GRADUATE_SCHOOL'
        ) {
          const updatedMessage = notification.message.replace(
            /\${examinerName}/g, // Regular expression to replace all instances
            examiner?.user?.displayName || examiner.fullName, // Replace with the tester's displayName
          )

          actions.push({
            ...notification,
            message: updatedMessage,
            relatedExaminerId: examiner.id,
          })
        }
      })
    }

    // Candidate actions
    if (isAssignedCandidate) {
      submission.thesisSubmissionExaminers.map((examiner) => {
        if (
          notification.actionId === ThesisActionNotificationActionIds.CANDIDATE_REVIEW_EXAMINER &&
          examiner.status === 'CANDIDATE_COI_REVIEW'
        ) {
          let updatedMessage
          const examinerName = examiner?.user?.displayName || examiner.fullName

          // Message update if the candidate has already provided COI statements previously
          if (
            examiner?.candidateCOIStatements &&
            Object.keys(examiner.candidateCOIStatements as Record<string, boolean>).length !== 0
          ) {
            updatedMessage = `The Graduate School requested changes to the details for the nomination of ${examinerName}. Your Principal Advisor has resubmitted the nomination. Please review and resubmit.`
          } else {
            updatedMessage = notification.message.replace(/\${examinerName}/g, examinerName)
          }

          actions.push({ ...notification, message: updatedMessage, relatedExaminerId: examiner.id })
        }
      })
    }

    // Examiner actions
    if (isThesisExaminer) {
      submission.thesisSubmissionExaminers.map((examiner) => {
        if (examiner?.userId === currentUser.id) {
          if (
            notification.actionId === ThesisActionNotificationActionIds.EXAMINER_PROVIDE_COI_DISCLOSURE &&
            examiner.status === 'EXAMINER_COI_REVIEW'
          ) {
            actions.push({ ...notification, relatedExaminerId: examiner.id })
          }

          if (
            notification.actionId === ThesisActionNotificationActionIds.EXAMINER_UPDATE_GRAD_SCHOOL_REQUESTED_DETAILS &&
            examiner.status === 'FINAL_CHANGE_REQUEST_BY_GRADUATE_SCHOOL'
          ) {
            actions.push({ ...notification, relatedExaminerId: examiner.id })
          }
        }
      })
    }

    // GS actions
    if (isGS) {
      submission.thesisSubmissionExaminers.map((examiner) => {
        if (
          notification.actionId === ThesisActionNotificationActionIds.GRAD_SCHOOL_REVIEW_EXAMINER &&
          (examiner.status === 'GRADUATE_SCHOOL_INITIAL_REVIEW' || examiner.status === 'GRADUATE_SCHOOL_FINAL_REVIEW')
        ) {
          actions.push({ ...notification, relatedExaminerId: examiner.id })
        }
      })
    }

    return null
  })

  return actions
}

/**
 * Get the thesis notification state of the notification for the user
 */
export const getThesisNotificationState = (
  currentUser: AuthUser,
  status?: Pick<ThesisSubmissionIterationStatus, 'status' | 'outcome'>,
) => {
  const roles = currentUser.roles

  let notificationState

  const iterationStateCode = getIterationState(status)?.code

  const iterationStateDetails = getIterationStateByCode(iterationStateCode)

  if (roles.includes('user-thesis-submission')) {
    notificationState = iterationStateDetails.candidateMessage.action
  } else if (roles.includes('user-thesis-principal-advisor')) {
    notificationState = iterationStateDetails.principalAdvisorMessage.action
  } else if (roles.includes('user-thesis-chair')) {
    notificationState = iterationStateDetails.chairMessage.action
  } else if (roles.includes('user-thesis-examiner')) {
    notificationState = iterationStateDetails.examinerMessage.action
  } else if (roles.includes('provost-user')) {
    notificationState = iterationStateDetails.provostMessage.action
  } else if (roles.includes('dean-user')) {
    notificationState = iterationStateDetails.deanMessage.action
  } else if (roles.includes('grad-school-user')) {
    notificationState = iterationStateDetails.graduateSchoolMessage.action
  } else {
    notificationState = NotificationState.NO_ACTION
  }

  return notificationState
}
